"use client";

import { useCallback, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import {
  ContentMenuContext,
  type ContentMenuSection,
} from "../../contexts/ContentMenuContext";

export type ContentMenuProviderProps = {
  children: ReactNode;
};

/**
 * Provider component that manages content menu sections.
 * Uses ref-based registration with microtask batching so that
 * multiple synchronous registerSection calls (e.g. 11 sections
 * mounting at once) produce a single state update and re-render.
 */
export function ContentMenuProvider({ children }: ContentMenuProviderProps) {
  const [sections, setSections] = useState<ContentMenuSection[]>([]);
  const sectionsRef = useRef<Map<string, ContentMenuSection>>(new Map());
  const batchPendingRef = useRef(false);

  const flush = useCallback(() => {
    if (batchPendingRef.current) return;
    batchPendingRef.current = true;
    Promise.resolve().then(() => {
      batchPendingRef.current = false;
      setSections(Array.from(sectionsRef.current.values()));
    });
  }, []);

  const registerSection = useCallback(
    (section: ContentMenuSection) => {
      sectionsRef.current.set(section.id, section);
      flush();
    },
    [flush],
  );

  const unregisterSection = useCallback(
    (id: string) => {
      if (sectionsRef.current.delete(id)) {
        flush();
      }
    },
    [flush],
  );

  const contextValue = useMemo(
    () => ({ registerSection, unregisterSection, sections }),
    [registerSection, unregisterSection, sections],
  );

  return (
    <ContentMenuContext.Provider value={contextValue}>
      {children}
    </ContentMenuContext.Provider>
  );
}
